// src/pages/Cars/CarDetails.jsx
import { ViewIcon } from "@chakra-ui/icons";
import {
    Badge,
    Box,
    Button,
    Center,
    Divider,
    HStack,
    Image,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import AccessDenied from "../../components/AccessDenied";
import Loading from "../../components/Loading/Loading";
import Pagination from "../../components/Pagination";
import TitleHeader from "../../components/TitleHeader";
import { hasPermission, useTableColors } from "../../Utils";

import { getCarById, getCarHistory } from "../../api";

const IMAGES_BASE = (process.env.REACT_APP_IMAGES || "").replace(/\/+$/, "");

export default function CarDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();

    const { borderColor, rowBgEven, rowBgOdd, rowHoverBg, subTextColor, theadBg } =
        useTableColors();

    const canView = hasPermission("viewCars");
    const canHistory = hasPermission("viewCarHistory");

    const toastCfg = useMemo(
        () => ({
            position: "top-right",
            isClosable: true,
            duration: 3500,
        }),
        []
    );

    const showError = (title, description) => {
        toast({
            ...toastCfg,
            status: "error",
            title: title || "Error",
            description: description || "Something went wrong",
        });
    };

    const toUpsUrl = (p) => {
        if (!p) return null;
        const s = String(p).trim();
        if (!s) return null;

        // already absolute
        if (s.startsWith("http://") || s.startsWith("https://")) return s;

        // "/uploads/cars/x.jpg" => `${IMAGES_BASE}/uploads/cars/x.jpg`
        return `${IMAGES_BASE}${s.replace(/^\/uploads(\/|$)/, "/uploads$1")}`;
    };

    // Image viewer modal
    const { isOpen: isImageOpen, onOpen: onOpenImage, onClose: onCloseImage } =
        useDisclosure();
    const [imageViewer, setImageViewer] = useState({ src: "", title: "" });

    const openImage = (src, title = "Photo") => {
        setImageViewer({ src, title });
        onOpenImage();
    };

    // Checkin photos modal (for a single pickup/dropoff row)
    const {
        isOpen: isCheckinOpen,
        onOpen: onOpenCheckin,
        onClose: onCloseCheckin,
    } = useDisclosure();
    const [checkinRow, setCheckinRow] = useState(null);

    const openCheckin = (row) => {
        setCheckinRow(row);
        onOpenCheckin();
    };

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [car, setCar] = useState(null);
    const [activeAssignment, setActiveAssignment] = useState(null);
    const [latestCheckin, setLatestCheckin] = useState(null);

    // history (backend returns assignments with pickup/dropoff inside)
    const [history, setHistory] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 20;

    const load = async (page = 1) => {
        setLoading(true);
        setError(null);

        try {
            const res = await getCarById(id);
            if (!res?.success) {
                setError(res?.message || "Failed to load car");
                setLoading(false);
                return;
            }

            const c = res.data?.car || null;
            const a = res.data?.activeAssignment || null;
            const lc = res.data?.latestCheckin || null;

            setCar(c);
            setActiveAssignment(a);
            setLatestCheckin(lc);

            if (canHistory) {
                const h = await getCarHistory(id, { page, limit: itemsPerPage });
                if (h?.success) {
                    const payload = h.data || {};
                    setHistory(payload.items || []);
                    setCurrentPage(Number(payload.currentPage || page));
                    setTotalPages(Number(payload.totalPages || 1));
                } else {
                    setHistory([]);
                    setCurrentPage(1);
                    setTotalPages(1);
                }
            } else {
                setHistory([]);
                setCurrentPage(1);
                setTotalPages(1);
            }
        } catch (e) {
            setError("Failed to load car");
        }

        setLoading(false);
    };

    useEffect(() => {
        load(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Flatten assignment history into raw "checkin rows" (pickup + dropoff as separate rows)
    const checkinRows = useMemo(() => {
        const rows = [];

        for (const a of history || []) {
            const courier = a?.courierId || null;

            if (a?.pickup) {
                rows.push({
                    _rowKey: a.pickup.checkinId || `${a._id}-pickup`,
                    type: "pickup",
                    courier,
                    assignedAt: a.assignedAt || null,
                    unassignedAt: a.unassignedAt || null,
                    checkin: a.pickup,
                });
            }
            if (a?.dropoff) {
                rows.push({
                    _rowKey: a.dropoff.checkinId || `${a._id}-dropoff`,
                    type: "dropoff",
                    courier,
                    assignedAt: a.assignedAt || null,
                    unassignedAt: a.unassignedAt || null,
                    checkin: a.dropoff,
                });
            }
        }

        // newest first by completedAt (createdAt)
        rows.sort((r1, r2) => {
            const t1 = new Date(r1?.checkin?.completedAt || 0).getTime();
            const t2 = new Date(r2?.checkin?.completedAt || 0).getTime();
            return t2 - t1;
        });

        return rows;
    }, [history]);

    if (loading) {
        return (
            <Center mt={"20%"}>
                <Loading />
            </Center>
        );
    }

    if (!canView) return <AccessDenied msg="Access denied" />;
    if (error) return <AccessDenied msg={error} />;
    if (!car) return <AccessDenied msg={"Car not found"} />;

    const assignedCourier = activeAssignment?.courierId || null;
    const assignedName =
        assignedCourier?.name ||
        `${assignedCourier?.firstName || ""} ${assignedCourier?.lastName || ""}`.trim();

    const currentOdo = latestCheckin?.odometerValue ?? car?.currentOdometer ?? null;

    return (
        <Box maxW="full" mx="auto">
            <VStack spacing={6} align="stretch">
                <TitleHeader title="Car Details" />

                {/* SUMMARY (no action buttons) */}
                <Box border="1px solid" borderColor={borderColor} borderRadius="md" p={4}>
                    <Stack spacing={3}>
                        <HStack justify="space-between" flexWrap="wrap">
                            <Text fontSize="lg" fontWeight="bold">
                                {car.year} {car.make} {car.model} — {car.plate}
                            </Text>

                            <HStack>
                                <Badge
                                    colorScheme={
                                        car.status === "active"
                                            ? "green"
                                            : car.status === "maintenance"
                                                ? "orange"
                                                : "red"
                                    }
                                >
                                    {car.status}
                                </Badge>
                                {car.deletedAt ? <Badge colorScheme="red">Deleted</Badge> : null}
                            </HStack>
                        </HStack>

                        <HStack spacing={8} flexWrap="wrap">
                            <Box>
                                <Text fontSize="sm" color={subTextColor}>
                                    VIN
                                </Text>
                                <Text fontWeight="bold">{car.vin || "-"}</Text>
                            </Box>

                            <Box>
                                <Text fontSize="sm" color={subTextColor}>
                                    Current Odometer
                                </Text>
                                <Text fontWeight="bold">
                                    {typeof currentOdo === "number" ? currentOdo.toLocaleString() : "-"}
                                </Text>
                            </Box>

                            <Box>
                                <Text fontSize="sm" color={subTextColor}>
                                    Assigned To
                                </Text>
                                {assignedCourier ? (
                                    <Text
                                        fontWeight="bold"
                                        cursor="pointer"
                                        onClick={() => navigate(`/couriers/details/${assignedCourier._id}`)}
                                    >
                                        {assignedName || "Unknown"}
                                    </Text>
                                ) : (
                                    <Text color={subTextColor}>-</Text>
                                )}
                            </Box>

                            <Box>
                                <Text fontSize="sm" color={subTextColor}>
                                    Last Check-in
                                </Text>
                                <Text fontWeight="bold">
                                    {latestCheckin?.completedAt
                                        ? new Date(latestCheckin.completedAt).toLocaleString()
                                        : "-"}
                                </Text>
                            </Box>
                        </HStack>

                        <Divider />
                        <Text fontSize="sm" color={subTextColor}>
                            History table below shows each pickup/dropoff entry as its own row.
                        </Text>
                    </Stack>
                </Box>

                {/* HISTORY TABLE (each pickup/dropoff entry as one row) */}
                {canHistory && (
                    <Box border="1px solid" borderColor={borderColor} borderRadius="md" p={4}>
                        <Text fontWeight="bold" mb={3}>
                            Assignment History (Paginated)
                        </Text>

                        {checkinRows.length ? (
                            <>
                                <Table size="sm">
                                    <Thead bg={theadBg}>
                                        <Tr>
                                            <Th>Type</Th>
                                            <Th>Courier</Th>
                                            <Th>Odometer</Th>
                                            <Th>Completed At</Th>
                                            <Th>Photos</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {checkinRows.map((r, idx) => {
                                            const c = r.courier;
                                            const courierName =
                                                c?.name ||
                                                `${c?.firstName || ""} ${c?.lastName || ""}`.trim() ||
                                                "Unknown";

                                            const odo =
                                                typeof r?.checkin?.odometerValue === "number"
                                                    ? r.checkin.odometerValue.toLocaleString()
                                                    : "-";

                                            const completedAt = r?.checkin?.completedAt
                                                ? new Date(r.checkin.completedAt).toLocaleString()
                                                : "-";

                                            const carPhotoCount = Array.isArray(r?.checkin?.carPhotos)
                                                ? r.checkin.carPhotos.length
                                                : 0;
                                            const odoPhotoCount = Array.isArray(r?.checkin?.odometerPhotos)
                                                ? r.checkin.odometerPhotos.length
                                                : 0;

                                            const hasPhotos = carPhotoCount + odoPhotoCount > 0;

                                            return (
                                                <Tr
                                                    key={r._rowKey || idx}
                                                    borderBottom="2px solid"
                                                    borderColor={borderColor}
                                                    _hover={{ bg: rowHoverBg, color: "white" }}
                                                    bg={idx % 2 === 0 ? rowBgEven : rowBgOdd}
                                                >
                                                    <Td>
                                                        <Badge colorScheme={r.type === "pickup" ? "green" : "orange"}>
                                                            {r.type}
                                                        </Badge>
                                                    </Td>

                                                    <Td>
                                                        {c?._id ? (
                                                            <Text
                                                                cursor="pointer"
                                                                fontWeight="bold"
                                                                onClick={() => navigate(`/couriers/details/${c._id}`)}
                                                            >
                                                                {courierName}
                                                            </Text>
                                                        ) : (
                                                            <Text fontWeight="bold">{courierName}</Text>
                                                        )}
                                                        <Text fontSize="xs" color={subTextColor}>
                                                            Cycle: {r?.checkin?.cycleId || "-"}
                                                        </Text>
                                                    </Td>

                                                    <Td>
                                                        <Text fontWeight="bold">{odo}</Text>
                                                    </Td>

                                                    <Td>
                                                        <Text>{completedAt}</Text>
                                                    </Td>

                                                    <Td>
                                                        <Button
                                                            size="xs"
                                                            leftIcon={<ViewIcon />}
                                                            colorScheme="blue"
                                                            variant={hasPhotos ? "solid" : "outline"}
                                                            onClick={() => openCheckin(r)}
                                                        >
                                                            View ({carPhotoCount + odoPhotoCount})
                                                        </Button>
                                                    </Td>
                                                </Tr>
                                            );
                                        })}
                                    </Tbody>
                                </Table>

                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    handlePageChange={(p) => load(p)}
                                />
                            </>
                        ) : (
                            <Text color={subTextColor}>No history.</Text>
                        )}
                    </Box>
                )}

                {!canHistory && <AccessDenied msg="No permission to view history" />}

                {/* CHECKIN PHOTOS MODAL */}
                <Modal
                    isOpen={isCheckinOpen}
                    size="6xl"
                    onClose={() => {
                        setCheckinRow(null);
                        onCloseCheckin();
                    }}
                >
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Check-in Photos</ModalHeader>
                        <ModalBody>
                            {checkinRow ? (
                                <Stack spacing={5}>
                                    <Box>
                                        <HStack justify="space-between" flexWrap="wrap">
                                            <HStack spacing={3} flexWrap="wrap">
                                                <Badge
                                                    colorScheme={checkinRow.type === "pickup" ? "green" : "orange"}
                                                >
                                                    {checkinRow.type}
                                                </Badge>
                                                <Text fontWeight="bold">
                                                    {checkinRow?.courier?.name ||
                                                        `${checkinRow?.courier?.firstName || ""} ${checkinRow?.courier?.lastName || ""
                                                            }`.trim() ||
                                                        "Unknown"}
                                                </Text>
                                            </HStack>
                                            <Text fontSize="sm" color={subTextColor}>
                                                {checkinRow?.checkin?.completedAt
                                                    ? new Date(checkinRow.checkin.completedAt).toLocaleString()
                                                    : "-"}
                                            </Text>
                                        </HStack>

                                        <Text fontSize="sm" color={subTextColor} mt={2}>
                                            Odometer:{" "}
                                            {typeof checkinRow?.checkin?.odometerValue === "number"
                                                ? checkinRow.checkin.odometerValue.toLocaleString()
                                                : "-"}
                                            {"  |  "}
                                            Cycle: {checkinRow?.checkin?.cycleId || "-"}
                                        </Text>
                                    </Box>

                                    <Divider />

                                    <Box>
                                        <Text fontSize="sm" color={subTextColor} mb={2}>
                                            Car Photos
                                        </Text>
                                        {Array.isArray(checkinRow?.checkin?.carPhotos) &&
                                            checkinRow.checkin.carPhotos.length ? (
                                            <HStack spacing={3} flexWrap="wrap">
                                                {checkinRow.checkin.carPhotos
                                                    .map(toUpsUrl)
                                                    .filter(Boolean)
                                                    .map((src, idx) => (
                                                        <Box
                                                            key={`${src}-${idx}`}
                                                            border="1px solid"
                                                            borderColor={borderColor}
                                                            borderRadius="md"
                                                            overflow="hidden"
                                                            cursor="pointer"
                                                            onClick={() => openImage(src, "Car Photo")}
                                                        >
                                                            <Image
                                                                src={src}
                                                                alt={`Car photo ${idx + 1}`}
                                                                objectFit="cover"
                                                                w="220px"
                                                                h="140px"
                                                            />
                                                        </Box>
                                                    ))}
                                            </HStack>
                                        ) : (
                                            <Text color={subTextColor}>No car photos.</Text>
                                        )}
                                    </Box>

                                    <Box>
                                        <Text fontSize="sm" color={subTextColor} mb={2}>
                                            Odometer Photos
                                        </Text>
                                        {Array.isArray(checkinRow?.checkin?.odometerPhotos) &&
                                            checkinRow.checkin.odometerPhotos.length ? (
                                            <HStack spacing={3} flexWrap="wrap">
                                                {checkinRow.checkin.odometerPhotos
                                                    .map(toUpsUrl)
                                                    .filter(Boolean)
                                                    .map((src, idx) => (
                                                        <Box
                                                            key={`${src}-${idx}`}
                                                            border="1px solid"
                                                            borderColor={borderColor}
                                                            borderRadius="md"
                                                            overflow="hidden"
                                                            cursor="pointer"
                                                            onClick={() => openImage(src, "Odometer Photo")}
                                                        >
                                                            <Image
                                                                src={src}
                                                                alt={`Odometer photo ${idx + 1}`}
                                                                objectFit="cover"
                                                                w="220px"
                                                                h="140px"
                                                            />
                                                        </Box>
                                                    ))}
                                            </HStack>
                                        ) : (
                                            <Text color={subTextColor}>No odometer photos.</Text>
                                        )}
                                    </Box>
                                </Stack>
                            ) : (
                                <Text color={subTextColor}>No data.</Text>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                onClick={() => {
                                    setCheckinRow(null);
                                    onCloseCheckin();
                                }}
                            >
                                Close
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* IMAGE VIEWER MODAL */}
                <Modal
                    isOpen={isImageOpen}
                    size="6xl"
                    onClose={() => {
                        setImageViewer({ src: "", title: "" });
                        onCloseImage();
                    }}
                >
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>{imageViewer.title || "Photo"}</ModalHeader>
                        <ModalBody>
                            {imageViewer.src ? (
                                <Box
                                    border="1px solid"
                                    borderColor={borderColor}
                                    borderRadius="md"
                                    overflow="hidden"
                                >
                                    <Image
                                        src={imageViewer.src}
                                        alt="Full"
                                        w="100%"
                                        maxH="80vh"
                                        objectFit="contain"
                                        onError={() => showError("Image Error", "Failed to load image.")}
                                    />
                                </Box>
                            ) : (
                                <Text color={subTextColor}>No image.</Text>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                onClick={() => {
                                    setImageViewer({ src: "", title: "" });
                                    onCloseImage();
                                }}
                            >
                                Close
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </VStack>
        </Box>
    );
}
